import { avSelectors } from "@minervaproject/avsubsystem";
import { useCallback, useEffect, useRef } from "react";

import {
  createDeviceErrorToast,
  createDeviceToast,
  createPoorConnectionToast,
} from "components/shared/toasts";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUserProperties } from "hooks/use_user_properties";
import {
  setUserCameraError,
  setUserConnectionQuality,
  setUserConnectionState,
  setUserIsPerformingAvCheck,
  setUserMicError,
  setUserStreamState,
} from "shared/reducers/classroom";
import { LocalModalContentType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { BandwidthModeType } from "shared/types/user";
import { localSelectors } from "store";
import { showLocalModal } from "store/reducers/local";
import { createMonitoringEvent } from "store/reducers/monitoring";
import { selectUserHasPoorConnection } from "store/selectors/classroom";

// Observes AV changes so toast notifications and warnings can be dispatched
// Separate from the participant A/V
function AvObserver() {
  const dispatch = useAppDispatch();

  // Current user info
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProperties(
    currentUserId,
    PROFILE_PROPERTIES.concat("is_performing_av_check", "bandwidth_mode"),
  );

  const isPerformingAvCheck = user?.is_performing_av_check;

  // Get most recent chat message
  const selectedCamera = avSelectors.useSelectedCamera();
  const selectedMic = avSelectors.useSelectedMic();
  const cameraDisabled = avSelectors.useCameraDisabled();
  const { cameraError, micError } = avSelectors.useDeviceGlobalErrors();
  const connectionState = avSelectors.useConnectionState();
  const streamState = avSelectors.useStreamState(currentUserId);
  const hasPoorConnection = useAppSelector(selectUserHasPoorConnection(currentUserId));

  const avConnectionQuality = avSelectors.useConnectionQuality();

  const previousCameraId = useRef("");
  const previousCameraError = useRef(false);
  const previousMicId = useRef("");
  const previousMicError = useRef(false);
  const previousCameraDisabled = useRef(false);

  const isAudioOnly =
    user?.bandwidth_mode === BandwidthModeType.AudioOnly ||
    user?.bandwidth_mode === BandwidthModeType.AudioOnlyForced;
  const handleOpenAVPreferences = useCallback(() => {
    dispatch(setUserIsPerformingAvCheck({ userId: currentUserId, isPerformingAvCheck: true }));
    dispatch(showLocalModal({ content: LocalModalContentType.AvPreferences }));
  }, [currentUserId, dispatch]);

  // Camera monitor logic
  useEffect(() => {
    if (isPerformingAvCheck) return;
    const cameraDeviceType = "camera";
    if (!!cameraError !== previousCameraError.current) {
      previousCameraError.current = !!cameraError;
      if (cameraError) {
        createDeviceErrorToast(cameraDeviceType, handleOpenAVPreferences);
        return;
      }
      // Recover from error
      if (!cameraError !== previousCameraError.current) {
        createDeviceToast(
          "Camera Disabled",
          "You can re-enable it anytime",
          false,
          cameraDeviceType,
        );
      }
    }

    if (cameraDisabled !== previousCameraDisabled.current) {
      previousCameraDisabled.current = cameraDisabled;
      if (cameraDisabled) {
        if (isAudioOnly) {
          createDeviceToast(
            "Camera Disabled",
            "Your camera has been disabled because you are in audio-only mode",
            false,
            cameraDeviceType,
          );
          return;
        }
        if (!cameraError) {
          createDeviceToast(
            "Camera Disabled",
            "You can re-enable it anytime",
            false,
            cameraDeviceType,
          );
        }
        return;
      }
      createDeviceToast("Camera Enabled", "You're now sharing your video", true, cameraDeviceType);
      previousCameraId.current = "force";

      return;
    }
    if (selectedCamera.selectedCam && selectedCamera.selectedCam.id !== previousCameraId.current) {
      // the first time this runs it will be when entering class, don't toast
      if (previousCameraId.current !== "") {
        createDeviceToast(
          "Change Detected",
          `You are now using camera: ${selectedCamera.selectedCam?.label}`,
          true,
          cameraDeviceType,
          handleOpenAVPreferences,
        );
      }
      previousCameraId.current = selectedCamera.selectedCam.id;
    }
  }, [
    selectedCamera,
    isPerformingAvCheck,
    cameraDisabled,
    isAudioOnly,
    handleOpenAVPreferences,
    cameraError,
  ]);

  // mic monitor logic
  useEffect(() => {
    if (isPerformingAvCheck) return;
    const micDeviceType = "mic";
    if (!!micError !== previousMicError.current) {
      previousMicError.current = !!micError;
      if (micError) {
        createDeviceErrorToast(micDeviceType, handleOpenAVPreferences);
        return;
      }
    }
    if (selectedMic.selectedMic && selectedMic.selectedMic.id !== previousMicId.current) {
      if (previousMicId.current !== "") {
        createDeviceToast(
          "Change Detected",
          `You are now using microphone: ${selectedMic.selectedMic?.label}`,
          true,
          micDeviceType,
          handleOpenAVPreferences,
        );
      }
      previousMicId.current = selectedMic.selectedMic.id;
    }
  }, [selectedMic, isPerformingAvCheck, handleOpenAVPreferences, micError]);

  useEffect(() => {
    dispatch(setUserCameraError({ userId: currentUserId, cameraError }));
  }, [dispatch, currentUserId, cameraError]);

  useEffect(() => {
    dispatch(setUserConnectionState({ userId: currentUserId, connectionState }));
  }, [dispatch, currentUserId, connectionState]);

  useEffect(() => {
    dispatch(setUserMicError({ userId: currentUserId, micError }));
  }, [dispatch, currentUserId, micError]);

  useEffect(() => {
    dispatch(setUserStreamState({ userId: currentUserId, streamState: streamState?.state }));
  }, [dispatch, currentUserId, streamState?.state]);

  useEffect(() => {
    dispatch(
      setUserConnectionQuality({ userId: currentUserId, connectionQuality: avConnectionQuality }),
    );
  }, [dispatch, currentUserId, avConnectionQuality]);

  useEffect(() => {
    if (hasPoorConnection) {
      createPoorConnectionToast("There are network issues that could affect your video quality");
    }
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.POOR_CONNECTION_INDICATOR_CHANGED,
        userId: currentUserId,
        contextData: { hasPoorConnection },
      }),
    );
  }, [dispatch, currentUserId, hasPoorConnection]);

  return null;
}

export { AvObserver };
